import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://musicalmeet.com';
const DEFAULT_IMAGE = `${BASE_URL}/LOGO.avif`;

/**
 * Reusable SEO component — renders <title>, meta, OG, Twitter, canonical, and optional JSON-LD.
 *
 * @param {string}  title       — Page-specific title (suffix " | MusicalMeet" appended automatically)
 * @param {string}  description — Meta description (max ~155 chars)
 * @param {string}  path        — Canonical path, e.g. "/all-artists" (default "/")
 * @param {string}  image       — Absolute OG image URL (default: site logo)
 * @param {string}  type        — OG type (default "website")
 * @param {boolean} noIndex     — Set true for auth/private pages
 * @param {object}  jsonLd      — Optional JSON-LD structured data object
 */
export default function SEO({
  title,
  description = 'MusicalMeet connects you with talented musicians, solo artists, and bands for events, parties, and jam sessions.',
  path = '/',
  image = DEFAULT_IMAGE,
  type = 'website',
  noIndex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | MusicalMeet` : 'MusicalMeet — Discover & Book Live Musicians Near You';
  const canonical = `${BASE_URL}${path}`;

  return (
    <Helmet>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="MusicalMeet" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@musicalmeeet" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
